var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { convertInviteeToParam, convertUserToInvitee } from './utils';
import { isoDurationOrNullIfZero } from './durationUtils';
import { EventSeriesOption, } from '../hooks/useEvent/useEventTypes';
import { normalizeDescriptionForEditor } from './parsers';
import { getInviteeRole } from './inviteeRoles';
export function buildCreateEventPayload(_a) {
    var _b, _c, _d;
    var formData = _a.formData, timezone = _a.timezone, calendarType = _a.calendarType, calendarMailboxAddress = _a.calendarMailboxAddress, requestOnlySpace = _a.requestOnlySpace;
    if (!formData || !timezone)
        return null;
    // A bookable event needs either a space or a personal calendar to land on.
    // When no space is selected we rely on the personal-booking calendar mailbox
    // resolved by CalendarIntegrationContext (empty string when none available).
    if (!((_b = formData.spaces) === null || _b === void 0 ? void 0 : _b.length) && !calendarMailboxAddress)
        return null;
    var includedIntegrations = formData.videoIntegrationId
        ? [formData.videoIntegrationId]
        : [];
    var invitees = formData.invitees
        .filter(function (invitee) { return invitee.email && invitee.user_id; })
        .map(convertInviteeToParam);
    return {
        title: formData.title || '',
        description: formData.description || '',
        space_invitees: ((_c = formData.spaces) !== null && _c !== void 0 ? _c : []).map(function (space) {
            var _a;
            var spaceBufferData = (_a = formData.spaceBuffers) === null || _a === void 0 ? void 0 : _a[space.id];
            return {
                id: space.id,
                name: space.name,
                desired_buffers: spaceBufferData
                    ? {
                        before_duration: isoDurationOrNullIfZero(spaceBufferData.beforeEvent),
                        after_duration: isoDurationOrNullIfZero(spaceBufferData.afterEvent),
                    }
                    : null,
            };
        }),
        startTime: formData.startTime.format(),
        endTime: formData.endTime.format(),
        recurrence: formData.recurrenceRule,
        timezone: timezone,
        invitees: invitees,
        attendeeCount: formData.attendeeCount,
        isPrivate: formData.isPrivateEvent,
        isAllDayEvent: formData.isAllDayEvent,
        preConfirm: (_d = formData.preConfirm) !== null && _d !== void 0 ? _d : false,
        includedIntegrations: includedIntegrations,
        calendarType: calendarType,
        calendarMailboxAddress: calendarMailboxAddress,
        uid: formData.uid,
        isRequestOnlySpace: !!requestOnlySpace,
    };
}
export function buildUpdateEventPayload(_a) {
    var _b;
    var formData = _a.formData, timezone = _a.timezone, calendarType = _a.calendarType, calendarMailboxAddress = _a.calendarMailboxAddress;
    if (!(formData === null || formData === void 0 ? void 0 : formData.eventId))
        return null;
    if (!formData.spaces || !timezone)
        return null;
    var invitees = formData.invitees
        .map(convertInviteeToParam)
        .filter(function (invitee) { return invitee !== undefined; });
    var includedIntegrations = formData.videoIntegrationId
        ? [formData.videoIntegrationId]
        : [];
    return {
        title: formData.title || '',
        description: formData.description,
        startTime: formData.startTime.format(),
        endTime: formData.endTime.format(),
        timezone: timezone,
        isAllDayEvent: formData.isAllDayEvent,
        isPrivate: formData.isPrivateEvent,
        invitees: __spreadArray([], __read(invitees), false),
        attendeeCount: formData.attendeeCount,
        includedIntegrations: includedIntegrations,
        recurrence: formData.recurrenceRule,
        calendarType: calendarType,
        calendarMailboxAddress: calendarMailboxAddress,
        space_invitees: (_b = formData.spaces) === null || _b === void 0 ? void 0 : _b.map(function (space) {
            var _a;
            var spaceBufferData = (_a = formData.spaceBuffers) === null || _a === void 0 ? void 0 : _a[space.id];
            return {
                id: space.id,
                name: space.name,
                /* Zero-length ISO durations (PT0M, P0D, etc.) become null via moment */
                desired_buffers: spaceBufferData
                    ? {
                        before_duration: isoDurationOrNullIfZero(spaceBufferData.beforeEvent),
                        after_duration: isoDurationOrNullIfZero(spaceBufferData.afterEvent),
                    }
                    : null,
            };
        }),
    };
}
export function shouldUpdateAsSpace(eventPermissions) {
    return !!(eventPermissions === null || eventPermissions === void 0 ? void 0 : eventPermissions.update_as_space) && !eventPermissions.update;
}
/**
 * Builds the `recurrence` value for an event-update PATCH request, or
 * `undefined` to omit the field entirely.
 *
 * The distinction between omitting the field and sending an empty array is
 * load-bearing and verified against the API:
 *  - field ABSENT  → no-op: the existing series recurrence is left untouched
 *    (and for `this-and-following` the original cadence is carried forward).
 *  - field `[]`    → the series recurrence is CLEARED.
 *  - recurrence on a single-occurrence update → rejected by validation.
 *
 * So the rules here are:
 *  - A single occurrence (THIS_EVENT) can never carry a recurrence rule → omit.
 *  - For series-level scopes (ALL_EVENTS / THIS_AND_FOLLOWING_EVENTS) the field
 *    is touched only when the user actually changed the recurrence. Unchanged →
 *    omit (leaves the cadence as-is; also avoids re-sending a round-tripped rule
 *    that could subtly differ from the stored one). Changed → send the new rule,
 *    or `[]` to deliberately clear it when the user removed recurrence.
 *
 * The rule still travels on the in-memory update payload (built by
 * buildUpdateEventPayload) so recurrence-change detection in useHandleEventUpdate
 * can compare it against the original.
 */
export function buildUpdateRecurrencePayload(eventSeriesOption, recurrence, recurrenceChanged) {
    // A single occurrence can never carry a recurrence rule.
    if (eventSeriesOption === EventSeriesOption.THIS_EVENT) {
        return undefined;
    }
    // Series-level update with no recurrence change: omit the field so the PATCH
    // leaves the existing series recurrence as-is.
    if (!recurrenceChanged) {
        return undefined;
    }
    // The recurrence changed: send the new rule, or an empty array to deliberately
    // clear it when the user removed recurrence entirely.
    return recurrence ? [recurrence.toString()] : [];
}
/**
 * Shapes an EventParams into the API request body for an event-update PATCH.
 *
 * The recurrence field is scope- and change-aware (see
 * buildUpdateRecurrencePayload): a single-occurrence (THIS_EVENT) update never
 * carries a recurrence rule, and a series-level update only sends one when the
 * recurrence actually changed. This is pinned by this builder's tests rather
 * than relying on the API call site staying correct.
 *
 * Note: the space_id is intentionally not part of this body — space moves are
 * handled by a separate /move endpoint.
 */
export function buildUpdateEventRequestBody(event, eventSeriesOption, recurrenceChanged) {
    var _a;
    return {
        start: {
            time_zone: event.timezone,
            date_time: event.startTime,
        },
        end: {
            time_zone: event.timezone,
            date_time: event.endTime,
        },
        title: event.title,
        description: event.description,
        is_all_day: event.isAllDayEvent,
        visibility: event.isPrivate ? 'private' : 'default',
        recurrence: buildUpdateRecurrencePayload(eventSeriesOption, event.recurrence, recurrenceChanged),
        invitees: event.invitees,
        space_invitees: (_a = event.space_invitees) === null || _a === void 0 ? void 0 : _a.map(function (space) {
            var _a;
            return ({
                space_id: space.id,
                desired_buffers: (_a = space.desired_buffers) !== null && _a !== void 0 ? _a : {
                    before_duration: null,
                    after_duration: null,
                },
            });
        }),
        integration_ids: event.includedIntegrations,
    };
}
export function transformDecoratedEventToUpdate(eventData) {
    var _a, _b;
    var spaces = eventData.space_invitees
        .filter(function (si) { return si.response_status !== 'declined'; })
        .map(function (si) {
        var _a, _b, _c, _d, _e;
        return ({
            id: si.space_id,
            image: si.space.image,
            locationId: si.space.location_id,
            levelId: si.space.level_id,
            inviteeId: si.id,
            name: si.space.name,
            isOrganizer: si.is_organizer,
            responseStatus: si.response_status,
            calendar: si.space.calendar
                ? {
                    calendarId: si.space.calendar.calendar_id,
                    createdAt: si.space.calendar.created_at,
                    remoteType: si.space.calendar.remote_type,
                    spaceId: si.space.calendar.space_id.toString(),
                    spaceResourceEmail: si.space.calendar.space_resource_email,
                }
                : null,
            description: si.space.description,
            capacity: si.space.capacity,
            isManaged: si.space.is_managed,
            amenities: si.space.space_amenities.map(function (a) { return ({
                id: a.id,
                quantity: a.quantity,
                amenityId: a.amenity_id,
                name: a.amenity.name,
            }); }),
            permissions: si.space.permissions,
            settings: si.space.settings,
            desired_buffers: ((_a = si.invitee_event) === null || _a === void 0 ? void 0 : _a.desired_buffers) && {
                before_duration: (_b = si.invitee_event.desired_buffers) === null || _b === void 0 ? void 0 : _b.before_duration,
                buffer_before_id: (_c = si.invitee_event.desired_buffers) === null || _c === void 0 ? void 0 : _c.buffer_before_id,
                after_duration: (_d = si.invitee_event.desired_buffers) === null || _d === void 0 ? void 0 : _d.after_duration,
                buffer_after_id: (_e = si.invitee_event.desired_buffers) === null || _e === void 0 ? void 0 : _e.buffer_after_id,
            },
        });
    });
    return {
        id: eventData.id,
        spaces: spaces,
        title: eventData.title,
        startTime: eventData.startTime,
        endTime: eventData.endTime,
        date: eventData.startTime,
        attendeeCount: (_a = eventData.reservation_approval) === null || _a === void 0 ? void 0 : _a.invitee_count,
        isAllDayEvent: eventData.is_all_day,
        isPrivate: eventData.visibility === 'private',
        uid: eventData.uid,
        space: eventData.space_id
            ? spaces === null || spaces === void 0 ? void 0 : spaces.find(function (s) { return s.id === (eventData === null || eventData === void 0 ? void 0 : eventData.space_id); })
            : spaces === null || spaces === void 0 ? void 0 : spaces[0],
        spaceIds: spaces === null || spaces === void 0 ? void 0 : spaces.map(function (s) { return s.id.toString(); }),
        spaceId: (_b = eventData === null || eventData === void 0 ? void 0 : eventData.space_id) === null || _b === void 0 ? void 0 : _b.toString(),
        seriesId: (eventData === null || eventData === void 0 ? void 0 : eventData.series_id) || undefined,
        recurrenceId: (eventData === null || eventData === void 0 ? void 0 : eventData.recurrence_id) || undefined,
        description: normalizeDescriptionForEditor(eventData.description),
        invitees: eventData.invitees
            .filter(function (inv) { return !inv.space_id; })
            .map(function (inv) {
            var _a, _b, _c, _d, _e;
            return (__assign(__assign({}, convertUserToInvitee({
                name: (_b = (_a = inv.user) === null || _a === void 0 ? void 0 : _a.name) !== null && _b !== void 0 ? _b : null,
                avatar: (_d = (_c = inv.user) === null || _c === void 0 ? void 0 : _c.avatar) !== null && _d !== void 0 ? _d : null,
                id: (_e = inv.user_id) !== null && _e !== void 0 ? _e : null,
                primaryEmail: { email: inv.email },
            })), { organizer: inv.is_organizer, 
                /* Display-only: creator label, organizer takes precedence */
                creator: getInviteeRole(eventData, inv, inv.is_organizer) === 'creator', response_status: inv.response_status }));
        })
            /* Order: organizer first, then creator, then everyone else */
            .sort(function (a, b) {
            var rank = function (inv) {
                return inv.organizer ? 0 : inv.creator ? 1 : 2;
            };
            return rank(a) - rank(b);
        }),
        eventPermissions: eventData.event_permissions,
        recurrenceRule: eventData.recurrenceRule,
        reservationApproval: eventData.reservation_approval,
        hasConfirmation: !!eventData.confirmation,
    };
}
